import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Users } from 'lucide-react';
import styles from './VolunteerSummary.module.css';
import { signupForVolunteerRole } from 'api/volunteerApi';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { toast } from 'react-toastify';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { UserPermissionLevel } from 'constants/auth';
import useGroupLabels from 'hooks/useGroupLabels';
import { groupOrderOfName } from 'utils/groupLabel';

const VolunteerSummary = ({
  meetId,
  clubId,
  userPermissionLevel,
  volunteerRequirements,
  onRequirementsUpdate,
}) => {
  // A group's heading reads "U11 Age Group", or "Y6 Year Group" for a club that groups by year level.
  const { words } = useGroupLabels(clubId);
  const [roles, setRoles] = useState(volunteerRequirements || []);
  const [signupLoading, setSignupLoading] = useState(false);
  const navigate = useNavigate();

  // Update roles when props change
  useEffect(() => {
    if (volunteerRequirements) {
      setRoles(volunteerRequirements);
    }
  }, [volunteerRequirements]);

  // Local list on purpose. The shared `fieldEvents` in config/admin/event
  // deliberately omits High Jump (different result model, see the comment
  // there). For ordering volunteer roles we want every field discipline, so
  // this mirrors the list VolunteerManagement keeps.
  const isFieldEvent = (roleName) => {
    const fieldEventNames = [
      'High Jump',
      'Long Jump',
      'Triple Jump',
      'Discus',
      'Javelin',
      'Shot Put',
      'Floppy Jump',
    ];
    return fieldEventNames.some((event) => roleName.includes(event));
  };

  const getGroupedAndSortedRoles = (roles) => {
    const general = [];
    const eventSpecific = [];
    const byAgeGroup = {};
    const byAthleteGroup = {};

    roles.forEach((role) => {
      if (role.roleCategory === 'General') {
        general.push(role);
      } else if (role.roleCategory === 'EventSpecific') {
        eventSpecific.push(role);
      } else if (role.roleCategory === 'AgeGroupSpecific') {
        if (role.groupType === 'AthleteGroup' && role.programGroupName) {
          // Athlete groups
          const key = role.programGroupName;
          if (!byAthleteGroup[key]) {
            byAthleteGroup[key] = {
              groupName: role.programGroupName,
              ageGroupCode: role.ageGroupCode,
              athleteCount: role.athleteCount,
              roles: [],
            };
          }
          byAthleteGroup[key].roles.push(role);
        } else if (role.groupType === 'AgeGroup' && role.programGroupName) {
          // Age groups - use programGroupName as the key and display name
          const key = role.programGroupName;
          if (!byAgeGroup[key]) {
            byAgeGroup[key] = {
              displayName: role.programGroupName, // This will be "U12 M", "U6", etc.
              ageGroupCode: role.ageGroupCode,
              roles: [],
            };
          }
          byAgeGroup[key].roles.push(role);
        }
      }
    });

    // General roles arrive in database insertion order (the API applies no
    // ordering). Sort by name like every other category so time-slot roles
    // such as "Canteen 12pm to 1pm" read in sequence.
    general.sort((a, b) => a.roleName.localeCompare(b.roleName));

    // Sort event-specific roles
    eventSpecific.sort((a, b) => {
      const aIsField = isFieldEvent(a.roleName);
      const bIsField = isFieldEvent(b.roleName);
      if (aIsField && !bIsField) return 1;
      if (!aIsField && bIsField) return -1;
      return a.roleName.localeCompare(b.roleName);
    });

    // Sort roles within each group
    Object.values(byAgeGroup).forEach((group) => {
      group.roles.sort((a, b) => a.roleName.localeCompare(b.roleName));
    });

    Object.values(byAthleteGroup).forEach((group) => {
      group.roles.sort((a, b) => a.roleName.localeCompare(b.roleName));
    });

    return { general, eventSpecific, byAgeGroup, byAthleteGroup };
  };

  const handleDirectSignup = async (role) => {
    setSignupLoading(true);

    try {
      await signupForVolunteerRole({
        meetVolunteerRequirementID: role.meetVolunteerRequirementID,
        notes: '',
      });

      toast.success(`Successfully signed up for ${role.roleName}!`);

      // Call parent's refresh function to reload all data
      if (onRequirementsUpdate) {
        await onRequirementsUpdate();
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || 'Failed to sign up for volunteer role.'
      );
    } finally {
      setSignupLoading(false);
    }
  };

  const renderRoleItem = (role) => {
    let statusClass = styles.empty;
    const statusText = `${role.numberCommitted} of ${role.numberRequired} filled`;

    if (role.isFullyStaffed) {
      statusClass = styles.complete;
    } else if (role.numberCommitted > 0) {
      statusClass = styles.partial;
    }

    return (
      <div
        className={styles.singleLineRoleItem}
        key={role.meetVolunteerRequirementID}
      >
        <div className={styles.roleLeftSection}>
          <span className={styles.infoIcon} title={role.description}>
            ℹ️
          </span>
          <span className={styles.roleName}>{role.roleName}</span>
          <span className={styles.pointsBadge}>
            ⭐ {role.hoursEarned} {role.hoursEarned === 1 ? 'hour' : 'hours'}
          </span>
        </div>

        <div className={styles.roleRightSection}>
          <span
            className={`${styles.statusText} ${
              role.isFullyStaffed ? styles.complete : ''
            }`}
          >
            {statusText}
          </span>
          <span className={`${styles.statusDot} ${statusClass}`}></span>
          {!role.isFullyStaffed && !role.isUserSignedUp ? (
            <button
              onClick={() => handleDirectSignup(role)}
              disabled={signupLoading}
              className={styles.signUpButton}
            >
              {signupLoading ? 'Signing Up...' : 'Sign me up!'}
            </button>
          ) : (
            <span className={styles.fullyStaffedLabel}>
              {role.isUserSignedUp ? 'Signed up' : 'Full'}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (!roles || roles.length === 0) {
    return (
      <div className={styles.volunteerSummary}>
        <div className={styles.volunteerHeader}>
          <span className={styles.btnIcon}>👥</span>
          Volunteers Needed
        </div>
        <div className={styles.volunteerContent}>
          <p style={{ textAlign: 'center', color: '#6c757d' }}>
            No volunteer requirements set for this meet.
          </p>
        </div>
      </div>
    );
  }

  const totalRequired = roles.reduce((sum, r) => sum + r.numberRequired, 0);
  const totalCommitted = roles.reduce((sum, r) => sum + r.numberCommitted, 0);
  const totalRemaining = roles.reduce((sum, r) => sum + r.numberRemaining, 0);
  const staffedPercent = totalRequired
    ? Math.round((totalCommitted / totalRequired) * 100)
    : 0;

  return (
    <div className={styles.volunteerSummary}>
      <div className={styles.volunteerHeader}>
        <span className={styles.btnIcon}>👥</span>
        Volunteers Needed
      </div>
      <div className={styles.volunteerContent}>
        <div className={styles.volunteerOverview}>
          <div className={styles.volunteerStat}>
            <span className={styles.volunteerStatNumber}>{totalCommitted}</span>
            <span className={styles.volunteerStatLabel}>Signed Up</span>
          </div>
          <div className={styles.volunteerStat}>
            <span className={styles.volunteerStatNumber}>{totalRequired}</span>
            <span className={styles.volunteerStatLabel}>Required</span>
          </div>
          <div className={styles.volunteerStat}>
            <span className={styles.volunteerStatNumber}>{totalRemaining}</span>
            <span className={styles.volunteerStatLabel}>Still Needed</span>
          </div>
          <div className={styles.volunteerStat}>
            <span className={styles.volunteerStatNumber}>
              {staffedPercent}%
            </span>
            <span className={styles.volunteerStatLabel}>Staffed</span>
          </div>
        </div>

        <div className={styles.volunteerProgress}>
          <div className={styles.progressLabel}>
            <span>Volunteer Staffing Progress</span>
            <span>
              {totalCommitted} of {totalRequired} positions filled
            </span>
          </div>
          <div className={styles.progressBarContainer}>
            <div
              className={`${styles.progressBarFill} ${staffedPercent < 100 ? styles.warning : styles.complete}`}
              style={{ width: `${staffedPercent}%` }}
            ></div>
          </div>
        </div>

        <div className={styles.volunteerRolesPreview}>
          {(() => {
            const { general, eventSpecific, byAgeGroup, byAthleteGroup } =
              getGroupedAndSortedRoles(roles);

            return (
              <>
                {/* General Roles */}
                {general.length > 0 && (
                  <div className={styles.roleCategory}>
                    <div className={styles.roleCategoryTitle}>General</div>
                    {general.map(renderRoleItem)}
                  </div>
                )}

                {/* Event-Specific Roles */}
                {eventSpecific.length > 0 && (
                  <div className={styles.roleCategory}>
                    <div className={styles.roleCategoryTitle}>
                      Event Officials
                    </div>
                    {eventSpecific.map(renderRoleItem)}
                  </div>
                )}

                {/* Athlete Group Specific Roles - sorted by age group, then by name */}
                {Object.values(byAthleteGroup)
                  .sort((a, b) => {
                    const aOrder = groupOrderOfName(a.ageGroupCode);
                    const bOrder = groupOrderOfName(b.ageGroupCode);
                    if (aOrder !== bOrder) return aOrder - bOrder;
                    return a.groupName.localeCompare(b.groupName);
                  })
                  .map((group) => (
                    <div key={group.groupName} className={styles.roleCategory}>
                      <div className={styles.roleCategoryTitle}>
                        {group.groupName}
                        <Users size={16} className={styles.groupIcon} />
                        {group.athleteCount && (
                          <span className={styles.memberCount}>
                            {group.athleteCount} athletes
                          </span>
                        )}
                      </div>
                      {group.roles.map(renderRoleItem)}
                    </div>
                  ))}

                {/* Age Group Specific Roles */}
                {Object.values(byAgeGroup)
                  .sort(
                    (a, b) =>
                      groupOrderOfName(a.ageGroupCode) -
                      groupOrderOfName(b.ageGroupCode)
                  )
                  .map((group) => (
                    <div
                      key={group.displayName}
                      className={styles.roleCategory}
                    >
                      <div className={styles.roleCategoryTitle}>
                        {group.displayName} {words.group}
                      </div>
                      {group.roles.map(renderRoleItem)}
                    </div>
                  ))}
              </>
            );
          })()}
        </div>

        <div className={styles.volunteerActions}>
          {userPermissionLevel >= UserPermissionLevel.Admin && (
            <CustomNewButton
              onClick={() =>
                navigate(routes.admin.volunteerManagement.url(clubId, meetId))
              }
              label="📋 Manage Signups"
            />
          )}
        </div>
      </div>
    </div>
  );
};

VolunteerSummary.propTypes = {
  meetId: PropTypes.string.isRequired,
  clubId: PropTypes.string.isRequired,
  userPermissionLevel: PropTypes.number.isRequired,
  volunteerRequirements: PropTypes.array,
  onRequirementsUpdate: PropTypes.func,
};

export default VolunteerSummary;
